<template>
	<main class="menuOnly">
		<router-view />
		<BannerCookie
			v-if="menusStore?.forceMenuId === null"
			@cookies-chosen="menusStore.saveData()"
			:base-color="menusStore?.business?.color"
			:class="{menuOnly: menusStore?.orderMode === 'none' || !menusStore?.mainMenuPage}"
		/>
		<AllergensSelector />
	</main>
	<NotificationManager />
</template>

<script>
import langObject from "@/book/assets/json/langs.json";
import langObjectAuto from "@/book/assets/json/auto.json";
import langObjectMenu from "@/new-menu/assets/json/langs.json";
import langObjectMenuAuto from "@/new-menu/assets/json/auto.json";

import {useLanguageStore, useNotificationStore, useScriptStore} from "@/shared/stores";
import API from "@/shared/helpers/API";
import BannerCookie from "@/new-menu/components/modals/BannerCookie.vue";
import AllergensSelector from "@/book/components/AllergensSelector.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useEvsStore, useMenusStore, useTranslationStore} from "@/new-menu/stores";
import {checkLocalStorageSupport} from "@/new-menu/helpers/page";
import NotificationManager from "@/new-menu/components/NotificationManager.vue";
import {setParam} from "@/shared/helpers/events";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const scriptStore = useScriptStore();
		const translationStore = useTranslationStore();
		const menusStore = useMenusStore();
		const evsStore = useEvsStore();
		return {languageStore, notificationStore, scriptStore, translationStore, menusStore, evsStore}
	},
	mixins: [WindowWidth],
	name: "App",
	components: {NotificationManager, AllergensSelector, BannerCookie},
	data() {
		return {
			subdomain: subdomain,
		}
	},
	created() {

		let protocol = ((TEST && LOCAL) ? "http://" : "https://");
		API.init(protocol+window.location.host, URL_HTML+"/console/login/");

		this.languageStore.automaticTranslation = true;
		this.languageStore.addStrings(langObjectMenu, "menu", langObjectMenuAuto);
		this.languageStore.addStrings(langObject, "reservations", langObjectAuto);
		this.languageStore.setLang(null,() => {
			this.translationStore.selectLanguage();
		});
		this.evsStore.initEvents();

		this.menusStore.initStyle();
		this.menusStore.loadSavedData();
		document.addEventListener('dblclick', function(event) {
			event.preventDefault();
		}, { passive: false });

		checkLocalStorageSupport();
	},
	methods: {
		addMetas() {

			let pageTitle = this.menusStore.business.name+" - "+this.languageStore.getString("reservations");
			let menuWord = this.languageStore.getString("reservations_system");

			let pageDesc = this.menusStore.business.description ? this.menusStore.business.descriptionTranslation : this.languageStore.getString('generic_page_description', [menuWord, this.menusStore.business.name]);
			let metaTags = [
				{name: "description", content: pageDesc},
				{property: "og:title", content: pageTitle},
				{property: "og:description", content: pageDesc},
				{name: "twitter:card", content: "summary"},
				{name: "twitter:description", content: pageDesc},
				{name: "robots", content: "index, follow"}
			];

			metaTags.forEach((tagData) => {
				let meta = document.querySelector("meta[property='"+tagData.property+"']");
				if(!meta) {
					meta = document.querySelector("meta[name='"+tagData.name+"']");
				}
				if(meta) {
					meta.setAttribute("content", tagData.content);
				} else {
					meta = document.createElement('meta');
					for (let key in tagData) {
						meta.setAttribute(key, tagData[key]);
					}
					document.head.appendChild(meta);
				}
			});

			if(this.languageStore.lang) {
				document.documentElement.lang = this.languageStore.lang;
			}

		}
	},
	mounted() {
		this.addMetas();

		// Set GTM business_id parameter
		if(this.menusStore?.business?.business_id) {
			setParam('business_id', ""+this.menusStore.business.business_id);
		}
	},
	watch: {
		'languageStore.lang': {
			handler: function (val, oldVal) {
				if(val) {
					document.documentElement.lang = val;
				}
			},
			deep: true
		},
	}
}
</script>

<style lang="scss">
:root {

	--padding-vertical: 12px;
	--padding-horizontal: 20px;
	--padding-horizontal-s: 12px;

	@media (max-width: 500px) {
		--padding-horizontal: 12px;
	}

}
button, [type="button"], [type="reset"], [type="submit"] {
	-webkit-appearance: button;
	background-color: transparent;
	background-image: none;
}
div, span, p, h1, h2, h3, h4, h5, h6, a, button, label, select {
	@include noSelect();
	font-family: var(--font-family) !important;
}
html, body {
	background-color: var(--theme-bg1-color) !important;
	@include hideScrollbar();
}
a {
	color: var(--theme-txt1-color) !important;

	&::after {
		background: var(--business-color) !important;
	}
}

p, span, h1, input, textarea, select, option, li, label, a, b, i, [type="button"], [type="reset"], [type="submit"] {

	&::selection {
		background: var(--business-color-alpha20);
	}

	&::-moz-selection {
		background: var(--business-color-alpha20);
	}

	&::placeholder {
		font-family: var(--font-family) !important;
	}

}

input, select, textarea, button {
	font-family: var(--font-family) !important;
}

img {
	font-size: 0;
	text-indent: 100%;
	white-space: nowrap;
	overflow: hidden;
}

.grecaptcha-badge {
	display: none;
}

.heatmapOpacity {
	opacity: 0.3;
}
</style>

<style lang="scss" scoped>
main {
	width: 100%;
	position: relative;
	background-color: var(--theme-bg1-color);
	display: flex;
	justify-content: flex-end;
	align-items: flex-start;

	&.menuOnly {
		justify-content: center;
	}

	.order-container {
		width: calc(100% / 3);
		max-width: 480px;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		position: fixed;
		left: calc((100% / 3) * 2);

		@media (max-width: 1024px) {
			display: none;
			position: relative;
			left: 0;
		}
	}

	.order-container-spacer {
		width: calc((100% / 3));

		@media (max-width: 1024px) {
			display: none;
		}
	}

	.container-cookies.menuOnly {
		left: 0;
		right: 0;
		margin: 0 auto;
	}

}
</style>